// src/components/admin/UserManagement.jsx

import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";

const SaveIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
    <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7z" />
    <path fillRule="evenodd" d="M3 6a1 1 0 011-1h12a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V6zm4 8a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1z" clipRule="evenodd" />
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m-4-6h4m4-6H5" />
  </svg>
);

const AVAILABLE_ROLES = ["aluno", "professor", "supervisor", "admin"];

const getRoleColor = (role) => {
  switch (role?.toLowerCase()) {
    case "admin": return "bg-red-100 text-red-800 border-red-300";
    case "supervisor": return "bg-indigo-100 text-indigo-800 border-indigo-300";
    case "professor": return "bg-yellow-100 text-yellow-800 border-yellow-300";
    default: return "bg-green-100 text-green-800 border-green-300";
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "approved": return "bg-green-100 text-green-800 border-green-300";
    case "pending": return "bg-yellow-100 text-yellow-800 border-yellow-300";
    case "rejected": return "bg-red-100 text-red-800 border-red-300";
    case "blocked": return "bg-gray-200 text-gray-700 border-gray-300";
    default: return "bg-gray-200 text-gray-700 border-gray-300";
  }
};

const translateStatus = (status) => {
  const translations = {
    approved: "APROVADO",
    pending: "PENDENTE",
    blocked: "BLOQUEADO",
    rejected: "REJEITADO"
  };
  return translations[status] || status?.toUpperCase() || "";
};

export default function UserManagement({ users, onDelete, onUpdateRole, onUpdateStatus }) {
  const { user: currentUser } = useAuth();
  const [selectedRole, setSelectedRole] = useState({});

  const isAdmin = currentUser?.role === "admin";
  const currentUserId = currentUser?.id;

  const handleLocalRoleChange = (id, role) =>
    setSelectedRole((prev) => ({ ...prev, [id]: role }));

  const formatLoginDate = (dateString) => {
    if (!dateString) return "Nunca";
    try {
      return new Date(dateString).toLocaleString("pt-BR");
    } catch {
      return dateString;
    }
  };

  if (!users || users.length === 0) {
    return <div className="p-6">Nenhum usuário encontrado.</div>;
  }

  return (
    <div className="bg-white rounded-xl">
      <h3 className="text-2xl font-bold mb-6">🧑‍🤝‍🧑 Gestão de Usuários</h3>
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase text-gray-500">Usuário</th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase text-gray-500">Perfil</th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase text-gray-500">Status</th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase text-gray-500">Último Login</th>
              {isAdmin && <th className="px-6 py-3 text-center text-xs font-bold uppercase text-gray-500">Ações</th>}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {users.map((u) => {
              const safeStatus = u.status || "pending";
              const currentSelectedRole = selectedRole[u.id] || u.role;
              const changedRole = currentSelectedRole !== u.role;
              const canModify = isAdmin && u.id !== currentUserId;

              return (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{u.name}</div>
                    <div className="text-xs text-gray-500">{u.email}</div>
                  </td>

                  <td className="px-6 py-4">
                    {canModify ? (
                      <select
                        value={currentSelectedRole}
                        onChange={(e) => handleLocalRoleChange(u.id, e.target.value)}
                        className={`text-xs font-bold p-1 rounded border ${getRoleColor(currentSelectedRole)}`}
                      >
                        {AVAILABLE_ROLES.map(role => (
                          <option key={role} value={role}>{role.toUpperCase()}</option>
                        ))}
                      </select>
                    ) : (
                      <span className={`px-2 py-1 rounded text-xs font-bold border ${getRoleColor(u.role)}`}>
                        {u.role.toUpperCase()}
                      </span>
                    )}
                  </td>

                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-bold border ${getStatusColor(safeStatus)}`}>
                      {translateStatus(safeStatus)}
                    </span>
                  </td>

                  <td className="px-6 py-4 text-xs text-gray-600">
                    {formatLoginDate(u.last_login)}
                  </td>

                  {isAdmin && (
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        {/* BOTÕES DE STATUS */}
                        {safeStatus === "pending" && (
                          <>
                            <button onClick={() => onUpdateStatus(u.id, "approved")} className="text-xs bg-green-500 text-white px-2 py-1 rounded">Aprovar</button>
                            <button onClick={() => onUpdateStatus(u.id, "rejected")} className="text-xs bg-red-500 text-white px-2 py-1 rounded">Rejeitar</button>
                          </>
                        )}
                        {safeStatus === "approved" && (
                          <button onClick={() => onUpdateStatus(u.id, "blocked")} className="text-xs bg-gray-700 text-white px-2 py-1 rounded">Bloquear</button>
                        )}
                        {safeStatus === "blocked" && (
                          <button onClick={() => onUpdateStatus(u.id, "approved")} className="text-xs bg-blue-500 text-white px-2 py-1 rounded">Ativar</button>
                        )}

                        {/* SALVAR ROLE */}
                        <button
                          onClick={() => onUpdateRole(u.id, currentSelectedRole)}
                          disabled={!changedRole}
                          className={`p-2 rounded-full ${changedRole ? "bg-indigo-600 text-white" : "text-gray-300 cursor-not-allowed"}`}
                        >
                          <SaveIcon />
                        </button>

                        {/* EXCLUIR */}
                        <button onClick={() => onDelete(u.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-full">
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}